<template>
  <ChannelAuthOrchestrator />

  <!-- Wait for channel and capability services to be ready before rendering -->
  <RouterView v-if="isCapabilitiesReady" />

  <LoyaltyPointsToasts v-if="!channelStore.isLeaderboardDisabled" />

  <AutoTranslateConfirmationModal
    v-model="translationStore.showAutoTranslateConfirmation"
  />

  <AutoTranslateConfirmationModal
    v-model="translationStore.showMobileAutoTranslateConfirmation"
  >
    <MobileAutoTranslateToggle />
  </AutoTranslateConfirmationModal>
</template>

<script setup lang="ts">
import { UserAuthenticationStatus, useUserStore } from "@/stores/user.store";
import { useChannelStore } from "@/stores/channel.store";
import { useCapabilitiesStore } from "@/stores/capabilities.store";
import ChannelAuthOrchestrator from "@/components/channel/auth/channel-auth-orchestrator.vue";

import { useSetupApiUserEvents } from "@/features/channel/services/use-api-user-events";
import { useSetupLoyaltyNotifications } from "@/features/channel/services/use-loyalty-notifications";
import LoyaltyPointsToasts from "./channel/loyalty/loyalty-points-toasts.vue";
import { useSetupReferralService } from "@/features/channel/services/use-referral-service";
import { useThemeStore } from "@/stores/theme.store";
import AutoTranslateConfirmationModal from "./channel/auto-translate-confirmation-modal.vue";
import MobileAutoTranslateToggle from "./channel/mobile-auto-translate-toggle.vue";
import { useTranslationStore } from "@/stores/translation.store";
import { computed, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import { RouteNames } from "@/router/app.router";
import type { ChannelPermission } from "@/generated/graphql";
import { useGoogleFont } from "@/composables/ui/use-google-font";
import { useEmbedAutoResize } from "@/composables/use-embed-auto-resize";

useEmbedAutoResize();
useSetupReferralService();
useSetupLoyaltyNotifications();
useSetupApiUserEvents();

const userStore = useUserStore();
const channelStore = useChannelStore();
const capabilitiesStore = useCapabilitiesStore();
const themeStore = useThemeStore();
const translationStore = useTranslationStore();

// Ensure all capability services are loaded before rendering RouterView
// This prevents child components from accessing uninitialized services
// We check:
// 1. Channel data is available (capabilities are known)
// 2. Capability services have finished loading
// Note: loadingChannel is intentionally NOT checked here — it becomes true
// during refetches (e.g. after login) and would blank the page unnecessarily.
// The channel !== null check already gates the initial load correctly.
const isCapabilitiesReady = computed(
  () => channelStore.channel !== null && capabilitiesStore.isReady,
);

const route = useRoute();
const router = useRouter();

useGoogleFont(() => themeStore.customSerifFont ?? null, "font-serif");

watch(
  () => userStore.authStatus,
  (status) => {
    // If route requires auth, and user is unauthenticated, redirect to channel
    if (
      route.meta.requiresAuth &&
      status === UserAuthenticationStatus.Unauthenticated
    ) {
      router.push({
        name: RouteNames.Channel,
        params: { channel: channelStore.channelSlug },
      });
    }
  },
  {
    immediate: true,
  },
);

watchEffect(async () => {
  if (route.meta.requiresPermission) {
    if (userStore.authStatus === UserAuthenticationStatus.Authenticated) {
      await channelStore.refreshPermissions();
      if (
        !channelStore.permissions[
          route.meta.requiresPermission as ChannelPermission
        ]
      ) {
        router.push({
          name: RouteNames.Channel,
        });
      }
    }
  }
});
</script>
